import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthRepository } from '../../../data/repositories';
import type { AuthProfile } from '../../models';
import {
  AUTH_STATE_CHANGE_EVENT,
  collectAuthProfiles,
  getAuthenticatedUserEmail,
  getUser,
  isAuthenticated,
  normalizeProfileKey,
} from '../../utils';

interface PrivateAppShellProps {
  children: ReactNode;
}

interface PrivateNavItem {
  accessProfiles?: string[];
  label: string;
  href: string;
  icon: string;
  aliases?: string[];
  disabled?: boolean;
  end?: boolean;
}

interface PrivateNavSection {
  label: string;
  items: PrivateNavItem[];
}

interface SidebarTooltipState {
  label: string;
  left: number;
  top: number;
}

const administrationAccessProfiles = ['ADMIN', 'GESTOR', 'GERENTE', 'SUPERVISOR'];
const parametersAccessProfiles = ['ADMIN', 'GESTOR', 'GERENTE', 'SUPERVISOR'];

const navigationSections: PrivateNavSection[] = [
  {
    label: 'Módulos',
    items: [
      { label: 'Dashboard', href: '/habita', icon: 'ri-dashboard-line', aliases: ['/dashboard'], end: true },
      { label: 'Pastas', href: '/habita/pastas', icon: 'ri-folder-3-line', aliases: ['/pastas', '/habita/cliente'] },
      { label: 'Kanban', href: '/habita/kanban', icon: 'ri-layout-grid-line', aliases: ['/kanban'] },
      { label: 'Novo processo', href: '/processo', icon: 'ri-add-circle-line', disabled: true },
      { label: 'Clientes', href: '/cadastro-clientes', icon: 'ri-user-add-line', disabled: true },
    ],
  },
  {
    label: 'Dashboards',
    items: [
      {
        label: 'Comercial',
        href: '/habita/comercial',
        icon: 'ri-line-chart-line',
        aliases: ['/comercial'],
        accessProfiles: ['ADMIN', 'GESTOR', 'GERENTE', 'SUPERVISOR', 'SDR'],
      },
      { label: 'Marketing', href: '/habita/marketing', icon: 'ri-megaphone-line', aliases: ['/marketing'] },
    ],
  },
  {
    label: 'Administração',
    items: [
      {
        label: 'Parâmetros',
        href: '/habita/parametros',
        icon: 'ri-equalizer-2-line',
        aliases: ['/parametros'],
        accessProfiles: parametersAccessProfiles,
      },
      {
        label: 'Usuários',
        href: '/habita/usuarios',
        icon: 'ri-user-settings-line',
        aliases: ['/usuarios'],
        accessProfiles: administrationAccessProfiles,
        disabled: true,
      },
    ],
  },
];
const privateNavigation = navigationSections.flatMap((section) => section.items);
const commercialNavItem = privateNavigation.find((item) => item.href === '/habita/comercial');
const marketingNavItem = privateNavigation.find((item) => item.href === '/habita/marketing');
const administrationNavItems = navigationSections
  .find((section) => section.label === 'Administração')
  ?.items ?? [];
const marketingAccessProfiles = new Set(['ADMIN', 'MARKETING', 'GERENTE', 'SUPERVISOR']);

const profileLabels: Record<AuthProfile, string> = {
  ADMIN: 'Administrador',
  GESTOR: 'Gestor',
  GERENTE: 'Gerente',
  SUPERVISOR: 'Supervisor',
  MARKETING: 'Marketing',
  SDR: 'SDR',
  CCA: 'CCA',
  VENDEDOR: 'Vendedor',
};

const ignoredProfileKeys = new Set(['ANON', 'AUTHENTICATED']);

function readInitialThemePreference(): boolean {
  if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return false;
  return window.localStorage.getItem('habita-theme') === 'dark';
}

function firstString(...values: unknown[]): string {
  const value = values.find((item) => item !== null && item !== undefined && String(item).trim() !== '');
  return value === undefined || value === null ? '' : String(value);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function formatProfileLabel(profile: string): string {
  const normalizedProfile = normalizeProfileKey(profile);
  return profileLabels[normalizedProfile as AuthProfile] ?? profile;
}

function resolveNormalizedProfiles(user: unknown): string[] {
  return Array.from(
    new Set(
      collectAuthProfiles(user)
        .map(normalizeProfileKey)
        .filter((profile) => profile && !ignoredProfileKeys.has(profile))
    )
  );
}

function resolveAuthUserRecord(value: unknown): Record<string, unknown> {
  const record = isRecord(value) ? value : {};
  const data = isRecord(record.data) ? record.data : {};
  const session = isRecord(record.session) ? record.session : {};
  const dataSession = isRecord(data.session) ? data.session : {};

  const candidates = [
    record.user,
    data.user,
    session.user,
    dataSession.user,
    record,
  ];

  return candidates.map((candidate) => isRecord(candidate) ? candidate : null).find(Boolean) ?? {};
}

function mergeUserIdentity(currentValue: unknown, nextValue: unknown): Record<string, unknown> {
  const currentUser = resolveAuthUserRecord(currentValue);
  const nextUser = resolveAuthUserRecord(nextValue);
  const currentMetadata = isRecord(currentUser.app_metadata) ? currentUser.app_metadata : {};
  const nextMetadata = isRecord(nextUser.app_metadata) ? nextUser.app_metadata : {};

  return {
    ...nextUser,
    email: firstString(nextUser.email, currentUser.email),
    app_metadata: {
      ...nextMetadata,
      display_name: firstString(nextMetadata.display_name, currentMetadata.display_name),
      email: firstString(nextMetadata.email, currentMetadata.email),
      full_name: firstString(nextMetadata.full_name, currentMetadata.full_name),
      name: firstString(nextMetadata.name, currentMetadata.name),
    },
  };
}

function buildInitials(value: string): string {
  const parts = value
    .replace(/@.*$/, '')
    .split(/[\s._-]+/)
    .filter(Boolean);

  if (parts.length === 0) return '?';
  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();
}

function resolveUserSummary(user: unknown) {
  const userRecord = resolveAuthUserRecord(user);
  const appMetadata = isRecord(userRecord.app_metadata) ? userRecord.app_metadata : {};
  const userMetadata = isRecord(userRecord.user_metadata) ? userRecord.user_metadata : {};
  const displayName = firstString(
    appMetadata.display_name,
    appMetadata.full_name,
    appMetadata.name,
    userMetadata.display_name,
    userMetadata.full_name,
    userMetadata.name,
    userRecord.display_name,
    userRecord.full_name,
    userRecord.name,
    getAuthenticatedUserEmail(user)
  );
  const uniqueProfiles = resolveNormalizedProfiles(user);
  const roleLabel = uniqueProfiles.length > 0
    ? uniqueProfiles.map(formatProfileLabel).join(', ')
    : 'Perfil não informado';
  const nameLabel = displayName || 'Nome não informado';

  return {
    initials: buildInitials(displayName),
    name: nameLabel,
    role: roleLabel,
  };
}

function isActivePath(pathname: string, item: PrivateNavItem): boolean {
  const paths = [item.href, ...(item.aliases ?? [])];
  if (item.end) return paths.includes(pathname);
  return paths.some((path) => pathname === path || pathname.startsWith(`${path}/`));
}

function ShellNavItem({
  item,
  active,
  collapsed,
  onNavigate,
  onTooltipClose,
  onTooltipOpen,
}: {
  item: PrivateNavItem;
  active: boolean;
  collapsed?: boolean;
  onNavigate: () => void;
  onTooltipClose: () => void;
  onTooltipOpen: (label: string, target: HTMLElement) => void;
}) {
  const baseClass =
    'group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm font-instrument transition-colors';
  const collapsedClass = collapsed ? 'justify-center px-0' : '';
  const tooltipHandlers = collapsed
    ? {
        onBlur: onTooltipClose,
        onFocus: (event: ReactFocusEvent<HTMLElement>) => onTooltipOpen(item.label, event.currentTarget),
        onMouseEnter: (event: ReactMouseEvent<HTMLElement>) => onTooltipOpen(item.label, event.currentTarget),
        onMouseLeave: onTooltipClose,
      }
    : {};

  if (item.disabled) {
    return (
      <span
        aria-disabled="true"
        className={`${baseClass} ${collapsedClass} cursor-not-allowed text-sidebar-foreground/45`}
        {...tooltipHandlers}
      >
        <i className={`${item.icon} text-lg`} aria-hidden />
        {collapsed && <span className="sr-only">{item.label}</span>}
        {!collapsed && (
          <>
            <span className="flex-1 truncate">{item.label}</span>
            <span className="rounded-full border border-sidebar-border px-2 py-0.5 text-[10px] uppercase text-sidebar-foreground/45">
              breve
            </span>
          </>
        )}
      </span>
    );
  }

  return (
    <Link
      to={item.href}
      onClick={onNavigate}
      className={
        active
          ? `${baseClass} ${collapsedClass} border-l-2 border-sidebar-primary bg-sidebar-accent text-sidebar-accent-foreground`
          : `${baseClass} ${collapsedClass} text-sidebar-foreground/75 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground`
      }
      {...tooltipHandlers}
    >
      <i className={`${item.icon} text-lg`} aria-hidden />
      {collapsed && <span className="sr-only">{item.label}</span>}
      {!collapsed && <span className="truncate">{item.label}</span>}
    </Link>
  );
}

export default function PrivateAppShell({ children }: PrivateAppShellProps) {
  const authRepository = useMemo(() => new AuthRepository(), []);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const [isDesktopNavCollapsed, setIsDesktopNavCollapsed] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(readInitialThemePreference);
  const [sidebarTooltip, setSidebarTooltip] = useState<SidebarTooltipState | null>(null);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<unknown>(() => getUser());
  const [hasLoadedCurrentUser, setHasLoadedCurrentUser] = useState(false);
  const userMenuContainerRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const userSummary = useMemo(() => resolveUserSummary(currentUser), [currentUser]);
  const userProfiles = useMemo(() => resolveNormalizedProfiles(currentUser), [currentUser]);
  const canAccessCommercial = userProfiles.some((profile) => commercialNavItem?.accessProfiles?.includes(profile));
  const canAccessMarketing = userProfiles.some((profile) => marketingAccessProfiles.has(profile));
  const canAccessAdministration = userProfiles.some((profile) => administrationAccessProfiles.includes(profile));
  const isMarketingOnlyUser = userProfiles.length === 1 && userProfiles[0] === 'MARKETING';
  const visibleNavigationSections = useMemo(
    () => navigationSections
      .filter((section) => section.label !== 'Administração' || canAccessAdministration)
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => {
          const isMarketingItem = item.href === '/habita/marketing';
          if (isMarketingOnlyUser) return isMarketingItem;
          if (isMarketingItem) return canAccessMarketing;
          if (item.accessProfiles) return userProfiles.some((profile) => item.accessProfiles?.includes(profile));
          return true;
        }),
      }))
      .filter((section) => section.items.length > 0),
    [canAccessAdministration, canAccessMarketing, isMarketingOnlyUser, userProfiles]
  );
  const visibleNavigation = useMemo(
    () => visibleNavigationSections.flatMap((section) => section.items),
    [visibleNavigationSections]
  );
  const homeHref = isMarketingOnlyUser ? '/habita/marketing' : '/habita';
  const homeLabel = isMarketingOnlyUser ? 'Marketing' : 'Dashboard';

  useEffect(() => {
    const root = document.documentElement;

    root.classList.toggle('dark', isDarkMode);
    if (typeof window.localStorage !== 'undefined') {
      window.localStorage.setItem('habita-theme', isDarkMode ? 'dark' : 'light');
    }

    return () => {
      root.classList.remove('dark');
    };
  }, [isDarkMode]);

  useEffect(() => {
    let cancelled = false;

    async function loadCurrentUser() {
      try {
        const apiUser = await authRepository.getCurrentUser();
        if (!cancelled && apiUser) {
          setCurrentUser((storedUser: unknown) => mergeUserIdentity(storedUser, apiUser));
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (!cancelled) {
          setHasLoadedCurrentUser(true);
        }
      }
    }

    void loadCurrentUser();

    return () => {
      cancelled = true;
    };
  }, [authRepository]);

  useEffect(() => {
    if (!hasLoadedCurrentUser) return;

    const isMarketingRoute = marketingNavItem ? isActivePath(location.pathname, marketingNavItem) : false;
    const isCommercialRoute = commercialNavItem ? isActivePath(location.pathname, commercialNavItem) : false;
    const isAdministrationRoute = administrationNavItems.some((item) => isActivePath(location.pathname, item));

    if (isMarketingOnlyUser && !isMarketingRoute) {
      navigate('/habita/marketing', { replace: true });
      return;
    }

    if (!canAccessMarketing && isMarketingRoute) {
      navigate('/habita', { replace: true });
      return;
    }

    if (!canAccessCommercial && isCommercialRoute) {
      navigate('/habita', { replace: true });
      return;
    }

    if (!canAccessAdministration && isAdministrationRoute) {
      navigate('/habita', { replace: true });
    }
  }, [
    canAccessAdministration,
    canAccessCommercial,
    canAccessMarketing,
    hasLoadedCurrentUser,
    isMarketingOnlyUser,
    location.pathname,
    navigate,
  ]);

  useEffect(() => {
    setIsUserMenuOpen(false);
    setSidebarTooltip(null);
  }, [location.hash, location.pathname, location.search]);

  useEffect(() => {
    if (!isDesktopNavCollapsed) {
      setSidebarTooltip(null);
    }
  }, [isDesktopNavCollapsed]);

  useEffect(() => {
    if (!isUserMenuOpen) return undefined;

    function closeUserMenu() {
      setIsUserMenuOpen(false);
      setSidebarTooltip(null);
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        closeUserMenu();
      }
    }

    function handlePointerDown(event: PointerEvent) {
      const target = event.target;
      if (target instanceof Node && userMenuContainerRef.current?.contains(target)) return;

      closeUserMenu();
    }

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('pointerdown', handlePointerDown, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [isUserMenuOpen]);

  useEffect(() => {
    if (!sidebarTooltip) return undefined;

    function closeSidebarTooltip() {
      setSidebarTooltip(null);
    }

    window.addEventListener('resize', closeSidebarTooltip);
    window.addEventListener('scroll', closeSidebarTooltip, true);

    return () => {
      window.removeEventListener('resize', closeSidebarTooltip);
      window.removeEventListener('scroll', closeSidebarTooltip, true);
    };
  }, [sidebarTooltip]);

  useEffect(() => {
    function handleAuthStateChange(event: Event) {
      const authEvent = event as CustomEvent<{ authenticated?: boolean }>;
      const authenticated = typeof authEvent.detail?.authenticated === 'boolean'
        ? authEvent.detail.authenticated
        : isAuthenticated();

      if (authenticated) {
        setCurrentUser(getUser());
        setHasLoadedCurrentUser(true);
        return;
      }

      const requestedPath = `${location.pathname}${location.search}${location.hash}`;
      setCurrentUser(null);
      setHasLoadedCurrentUser(true);
      setIsMobileNavOpen(false);
      setIsUserMenuOpen(false);
      navigate(
        { pathname: '/login', search: `?redirect=${encodeURIComponent(requestedPath)}` },
        { replace: true, state: { from: requestedPath } }
      );
    }

    window.addEventListener(AUTH_STATE_CHANGE_EVENT, handleAuthStateChange);

    return () => {
      window.removeEventListener(AUTH_STATE_CHANGE_EVENT, handleAuthStateChange);
    };
  }, [location.hash, location.pathname, location.search, navigate]);

  async function handleLogout() {
    setIsMobileNavOpen(false);
    setIsUserMenuOpen(false);
    setSidebarTooltip(null);

    const result = await authRepository.logout();
    if (result.error) {
      console.error(result.error);
    }
    navigate('/login', { replace: true });
  }

  const currentPage = useMemo(() => {
    return (
      visibleNavigation.find((item) => isActivePath(location.pathname, item))?.label ??
      'Habita4u'
    );
  }, [location.pathname, visibleNavigation]);

  function openSidebarTooltip(label: string, target: HTMLElement) {
    target.removeAttribute('title');
    const rect = target.getBoundingClientRect();
    setSidebarTooltip({
      label,
      left: rect.right + 12,
      top: rect.top + rect.height / 2,
    });
  }

  function renderSidebar(isMobile = false) {
    const collapsed = !isMobile && isDesktopNavCollapsed;
    const closeSidebarAfterNavigation = () => {
      setIsUserMenuOpen(false);
      setSidebarTooltip(null);

      if (isMobile) {
        setIsMobileNavOpen(false);
        return;
      }

      setIsDesktopNavCollapsed(true);
    };

    return (
      <aside
        className={`relative z-[70] flex h-full shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200 ${
          collapsed ? 'w-16' : 'w-64'
        }`}
      >
        <div
          className={`flex items-center border-b border-sidebar-border bg-primary text-[#0d3512] ${
            collapsed ? 'h-24 flex-col justify-center gap-1.5 px-2' : 'h-16 gap-2.5 px-4'
          }`}
        >
          {collapsed ? (
            <Link
              to={homeHref}
              onClick={closeSidebarAfterNavigation}
              className="group relative rounded-md p-1.5 transition-colors hover:bg-[#0d3512]/10"
              onBlur={() => setSidebarTooltip(null)}
              onFocus={(event) => openSidebarTooltip(homeLabel, event.currentTarget)}
              onMouseEnter={(event) => openSidebarTooltip(homeLabel, event.currentTarget)}
              onMouseLeave={() => setSidebarTooltip(null)}
            >
              <img src="/logo.svg" alt="" aria-hidden className="h-8 w-8 object-contain" />
              <span className="sr-only">{homeLabel}</span>
            </Link>
          ) : (
            <Link
              to={homeHref}
              onClick={closeSidebarAfterNavigation}
              className="flex min-w-0 flex-1 items-center"
            >
              <img src="/img/habita4u.svg" alt="Habita4u" className="h-7 w-auto min-w-0" />
            </Link>
          )}

          {isMobile ? (
            <button
              type="button"
              onClick={() => setIsMobileNavOpen(false)}
              className="rounded-md p-2 text-[#0d3512]/70 hover:bg-[#0d3512]/10 hover:text-[#0d3512]"
              aria-label="Fechar menu"
            >
              <i className="ri-close-line text-xl" aria-hidden />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setIsDesktopNavCollapsed((value) => !value)}
              className="rounded-md p-2 text-[#0d3512]/70 hover:bg-[#0d3512]/10 hover:text-[#0d3512]"
              aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}
            >
              <i
                className={`${collapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'} text-lg`}
                aria-hidden
              />
            </button>
          )}
        </div>

        <nav className={`flex-1 px-3 py-4 ${collapsed ? 'overflow-visible' : 'overflow-y-auto'}`}>
          <div className="space-y-5">
            {visibleNavigationSections.map((section) => (
              <div key={section.label} className="space-y-1.5">
                {collapsed ? (
                  <div className="mx-auto h-px w-7 bg-sidebar-border" aria-hidden />
                ) : (
                  <p className="px-3 font-instrument text-[10px] font-semibold uppercase tracking-[0.12em] text-sidebar-foreground/45">
                    {section.label}
                  </p>
                )}

                <div className="space-y-1">
                  {section.items.map((item) => (
                    <ShellNavItem
                      key={item.href}
                      item={item}
                      active={isActivePath(location.pathname, item)}
                      collapsed={collapsed}
                      onNavigate={closeSidebarAfterNavigation}
                      onTooltipClose={() => setSidebarTooltip(null)}
                      onTooltipOpen={openSidebarTooltip}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </nav>

        <div
          ref={userMenuContainerRef}
          className="relative border-t border-sidebar-border p-4"
          onBlur={(event) => {
            const currentTarget = event.currentTarget;

            window.requestAnimationFrame(() => {
              if (currentTarget.contains(document.activeElement)) return;

              setIsUserMenuOpen(false);
              setSidebarTooltip(null);
            });
          }}
        >
          {isUserMenuOpen && (
            <div
              role="menu"
              className={`absolute z-[90] overflow-hidden rounded-lg border border-sidebar-border bg-card text-textPrimary shadow-xl shadow-slate-900/15 ring-1 ring-black/5 ${
                collapsed ? 'bottom-0 left-full ml-2 w-56' : 'bottom-full left-4 right-4 mb-2'
              }`}
            >
              <div className="border-b border-border-subtle px-3 py-3">
                <p className="truncate font-instrument text-sm font-semibold text-textPrimary">{userSummary.name}</p>
                <p className="truncate font-instrument text-xs text-textSecondary">{userSummary.role}</p>
              </div>
              <button
                type="button"
                role="menuitem"
                onClick={handleLogout}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left font-instrument text-sm text-red-600 transition-colors hover:bg-red-500/10 hover:text-red-700 dark:text-red-300 dark:hover:text-red-200"
              >
                <i className="ri-logout-box-r-line text-lg" aria-hidden />
                <span>Sair</span>
              </button>
            </div>
          )}

          <button
            type="button"
            aria-label="Abrir menu do usuário"
            aria-haspopup="menu"
            aria-expanded={isUserMenuOpen}
            onBlur={() => setSidebarTooltip(null)}
            onClick={() => {
              setSidebarTooltip(null);
              setIsUserMenuOpen((value) => !value);
            }}
            onFocus={(event) => {
              if (collapsed && !isUserMenuOpen) openSidebarTooltip(userSummary.name, event.currentTarget);
            }}
            onMouseEnter={(event) => {
              if (collapsed && !isUserMenuOpen) openSidebarTooltip(userSummary.name, event.currentTarget);
            }}
            onMouseLeave={() => setSidebarTooltip(null)}
            className={`group/user relative flex items-center font-instrument transition-colors ${
              collapsed
                ? 'mx-auto h-10 w-10 justify-center rounded-full hover:bg-sidebar-accent'
                : 'w-full gap-3 rounded-lg px-2 py-2 text-left hover:bg-sidebar-accent hover:text-sidebar-accent-foreground'
            }`}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-[#0d3512]">
              {userSummary.initials}
            </span>
            {!collapsed && (
              <div className="min-w-0 flex-1 text-xs">
                <p className="truncate font-instrument font-medium text-sidebar-foreground">{userSummary.name}</p>
                <p className="truncate font-instrument text-sidebar-foreground/60">{userSummary.role}</p>
              </div>
            )}
            {!collapsed && (
              <i
                className={`ri-arrow-up-s-line text-base text-sidebar-foreground/50 transition-transform ${
                  isUserMenuOpen ? 'rotate-180' : ''
                }`}
                aria-hidden
              />
            )}
          </button>
        </div>
      </aside>
    );
  }

  return (
    <div className="min-h-screen bg-background text-textPrimary lg:flex">
      <div className="hidden lg:sticky lg:top-0 lg:z-[100] lg:block lg:h-screen lg:self-start">
        {renderSidebar()}
      </div>

      {isMobileNavOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <button
            type="button"
            aria-label="Fechar menu"
            className="absolute inset-0 bg-black/45"
            onClick={() => setIsMobileNavOpen(false)}
          />
          <div className="relative h-full">{renderSidebar(true)}</div>
        </div>
      )}

      <div className="relative z-0 flex min-h-screen min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-4 border-b border-border-subtle bg-card px-4 sm:px-6">
          <button
            type="button"
            onClick={() => setIsMobileNavOpen(true)}
            className="rounded-lg p-2 text-textPrimary hover:bg-accent lg:hidden"
            aria-label="Abrir menu"
          >
            <i className="ri-menu-line text-2xl" aria-hidden />
          </button>

          <img src="/img/habita4u.svg" alt="Habita4u" className="hidden h-7 w-auto shrink-0 sm:block lg:hidden" />

          <div className="min-w-0">
            <h1 className="truncate font-instrument text-base font-semibold text-textPrimary">
              {currentPage}
            </h1>
          </div>

          <div className="ml-auto hidden items-center gap-1 sm:flex">
            <button
              type="button"
              className="rounded-lg p-2 text-textSecondary hover:bg-accent hover:text-textPrimary"
              aria-label={isDarkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}
              title={isDarkMode ? 'Ativar modo claro' : 'Ativar modo escuro'}
              onClick={() => setIsDarkMode((value) => !value)}
            >
              <i className={`${isDarkMode ? 'ri-sun-line' : 'ri-moon-line'} text-xl`} aria-hidden />
            </button>

            <button
              type="button"
              className="rounded-lg p-2 text-textSecondary hover:bg-accent hover:text-textPrimary"
              aria-label="Notificações"
            >
              <i className="ri-notification-3-line text-xl" aria-hidden />
            </button>
          </div>
        </header>

        <main className="flex-1 overflow-x-hidden p-4 sm:p-6">{children}</main>
      </div>
      {sidebarTooltip && (
        <div
          className="pointer-events-none fixed z-[120] -translate-y-1/2 whitespace-nowrap rounded-md bg-card px-2.5 py-1.5 font-instrument text-xs font-medium text-textPrimary shadow-xl ring-1 ring-black/10"
          style={{
            left: sidebarTooltip.left,
            top: sidebarTooltip.top,
          }}
        >
          {sidebarTooltip.label}
        </div>
      )}
    </div>
  );
}
